/* eslint-disable no-restricted-globals */
import axios from "axios";
import { GRAPH_QL_API_URL, REJECT_UNAUTHORIZED } from "./config.js";
import AuthHeader from "tools/authHeader";
import {
  platformNotifMessages,
  logout,
  debugLog
} from "tools/commonFunctions";
import i18n from "localization/i18n";
import {
  CLIENT_TOKEN_EXP_KEY,
  CLIENT_TOKEN_KEY,
  TOKEN_SKEW_MS,
  getGraphQlAuthErrorMessage,
  hasUserSession,
  isAccessTokenExpired,
  isGetClientTokenRequest,
  isGraphQlNotAuthorised,
  isLoginRoute,
  isUserSessionExpired,
  resolveUnauthorisedAction,
  clearExpiredUserSession,
  createGraphQlUnauthorisedError
} from "tools/authToken";
const https = require('https-browserify')

const LAINOTIK_CLIENTID = "6fc07d90-a5b2-4f70-bf15-d315a2739354"

export default class httpRequest {

  constructor() {
    this._clientTokenPromise = null;
    this._loggingOut = false;
    this.instance = axios.create({
      headers: {
        'Content-Type': 'application/json',
        'Client-Authorization': ''
      },
      httpsAgent: new https.Agent({
        rejectUnauthorized: REJECT_UNAUTHORIZED
      })
    });
    this.interceptor();
    this.ensureClientToken();
  }

  getAuthToken() {
    return localStorage.getItem(CLIENT_TOKEN_KEY);
  }

  getTokenExpiration() {
    return localStorage.getItem(CLIENT_TOKEN_EXP_KEY);
  }

  isTokenExpired() {
    return isAccessTokenExpired(this.getAuthToken(), this.getTokenExpiration(), TOKEN_SKEW_MS);
  }

  clearClientToken() {
    localStorage.removeItem(CLIENT_TOKEN_KEY);
    localStorage.removeItem(CLIENT_TOKEN_EXP_KEY);
  }

  async ensureClientToken() {
    if (this.getAuthToken() && !this.isTokenExpired()) {
      return this.getAuthToken();
    }

    if (this.getAuthToken() && this.isTokenExpired()) {
      debugLog("http", "Client token expired, clearing and requesting new one");
      this.clearClientToken();
    }

    if (!this._clientTokenPromise) {
      this._clientTokenPromise = this.getClientToken().finally(() => {
        this._clientTokenPromise = null;
      });
    }
    await this._clientTokenPromise;
    return this.getAuthToken();
  }

  async getClientToken() {
    if (this.getAuthToken() && !this.isTokenExpired()) {
      return this.getAuthToken();
    }

    const GET_TOKEN = {
      "query": `mutation {
            getClientToken(
              id: "${LAINOTIK_CLIENTID}"
            )
            {
              backresponse {
                code
                error
                message
                messageinfo {
                  messagelevel
                  messagetitle
                  message
                }
              }
              clientaccesstoken {
                accesstoken
                expirationdate
                token_type
              }
            }
          }`
    };

    const maxAttempts = 3;
    let lastError = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const backresponse = await this.instance.post(GRAPH_QL_API_URL, GET_TOKEN);
        const payload = backresponse && backresponse.data && backresponse.data.data
          ? backresponse.data.data.getClientToken
          : null;
        if (payload && payload.clientaccesstoken && payload.clientaccesstoken.accesstoken) {
          const tokenType = payload.clientaccesstoken.token_type || "Bearer";
          const authToken = tokenType + " " + payload.clientaccesstoken.accesstoken;
          localStorage.setItem(CLIENT_TOKEN_KEY, authToken);
          if (payload.clientaccesstoken.expirationdate) {
            localStorage.setItem(CLIENT_TOKEN_EXP_KEY, String(payload.clientaccesstoken.expirationdate));
            debugLog("http", "Client token obtained, expires:", payload.clientaccesstoken.expirationdate);
          }
          return authToken;
        }
        lastError = new Error(
          (payload && payload.backresponse && payload.backresponse.message) ||
          "Client token no disponible"
        );
      } catch (error) {
        lastError = error;
        debugLog("http", "Error on the client token request:", error && error.message ? error.message : error);
      }
    }

    const useMocks = String(process.env.REACT_APP_USE_MOCKS || "").trim() === "1";
    const onLoginPage = isLoginRoute(typeof window !== "undefined" ? window.location.pathname : "/");
    if (!useMocks && !onLoginPage) {
      platformNotifMessages(
        "DANGER",
        i18n.t("common.error"),
        lastError && lastError.message ? lastError.message : "Client token no disponible"
      );
    } else {
      debugLog("http", "Client token unavailable after retries", lastError && lastError.message);
    }
    return null;
  }

  sessionLogout(title, message) {
    if (this._loggingOut) return;
    this._loggingOut = true;
    platformNotifMessages(
      "DANGER",
      title,
      message
    );
    setTimeout(() => {
      logout();
    }, 3000);
  }

  applyAuthHeaders(config, clientToken) {
    const authHeaders = AuthHeader(clientToken);
    if (config.headers && typeof config.headers.set === "function") {
      Object.keys(authHeaders).forEach((key) => {
        config.headers.set(key, authHeaders[key]);
      });
      return config;
    }
    config.headers = {
      ...config.headers,
      ...authHeaders
    };
    return config;
  }

  interceptor() {
    const self = this;

    this.instance.interceptors.request.use(async function (config) {
      if (isGetClientTokenRequest(config)) {
        if (config.headers && typeof config.headers.set === "function") {
          config.headers.set("Client-Authorization", "");
          config.headers.set("Authorization", "");
        } else {
          config.headers = {
            ...config.headers,
            "Client-Authorization": "",
            Authorization: ""
          };
        }
        return config;
      }
      if (isLoginRoute(typeof window !== "undefined" ? window.location.pathname : "/")) {
        clearExpiredUserSession();
      }
      await self.ensureClientToken();
      return self.applyAuthHeaders(config, self.getAuthToken());
    });

    this.instance.interceptors.response.use(async function (response) {
      if (response.status === 200 && isGraphQlNotAuthorised(response)) {
        return self.handleUnauthorisedResponse(response);
      }
      if (response.status === 200) {
        debugLog("http", "OK 200");
      }
      return response;
    }, function (error) {
      const status = error && error.response ? error.response.status : undefined;
      if (status === 400) {
        debugLog("http", "BAD REQUEST");
      }
      return Promise.reject(error);
    });
  }

  async handleUnauthorisedResponse(response) {
    const config = response.config || {};
    const action = resolveUnauthorisedAction({
      alreadyRetried: Boolean(config._authRetry),
      isClientTokenRequest: isGetClientTokenRequest(config),
      loginPage: isLoginRoute(typeof window !== "undefined" ? window.location.pathname : "/"),
      userSessionPresent: hasUserSession(),
      userSessionExpired: isUserSessionExpired()
    });
    const authMessage = getGraphQlAuthErrorMessage(response) || "Not Authorised!";

    debugLog("http", "GraphQL not authorised, action:", action, authMessage);

    if (action === "refresh_client_and_retry") {
      this.clearClientToken();
      if (isLoginRoute(typeof window !== "undefined" ? window.location.pathname : "/")) {
        clearExpiredUserSession();
      }
      const nextToken = await this.ensureClientToken();
      if (!nextToken) {
        return this.failSoftUnauthorised(response, authMessage);
      }
      config._authRetry = true;
      this.applyAuthHeaders(config, nextToken);
      return this.instance.request(config);
    }

    if (action === "logout") {
      this.sessionLogout(authMessage, i18n.t("common.notAuthorised"));
      return Promise.reject(createGraphQlUnauthorisedError(authMessage, response, {
        sessionLogoutHandled: true
      }));
    }

    return this.failSoftUnauthorised(response, authMessage);
  }

  failSoftUnauthorised(response, authMessage) {
    debugLog("http", "Keeping session after unauthorised GraphQL response");
    return Promise.reject(createGraphQlUnauthorisedError(authMessage, response));
  }

  async httpPostRequestGraphQL(sendJsonData) {
    await this.ensureClientToken();
    return this.instance.post(GRAPH_QL_API_URL, sendJsonData, { headers: AuthHeader(this.getAuthToken()) });
  }
}
